// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar, type OptionsObject } from 'notistack';
import { advisoryApi as advisoryApiV2 } from '@shared/data-access/api/brokerage/advisory-api.v2';
import {
  useAdvisors,
  type AdvisorRow,
} from '@shared/advisory/aggregation/hooks/use-advisors';
import { useConfirmPopup } from '@shared/ui-kit/components/popups';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import type {
  UnshareAdvisoryUsersAccountsParams,
  UnshareAdvisoryUsersAccountsRequestBody,
} from '@shared/data-access/types/brokerage-types';

const commonSnackProps: OptionsObject = {
  variant: 'success',
  anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
};

type SharedAccount = Pick<
  AdvisorRow,
  'iban' | 'institutionId' | 'institutionName' | 'name'
>;

/** Advisors list + unshare (no advisory proposals — those move to CW1-2945). */
export const useManageAccess = () => {
  const { t } = useTranslation();
  const showConfirm = useConfirmPopup();
  const { enqueueSnackbar } = useSnackbar();

  const { advisorsData, status, hasData, onRefresh } = useAdvisors();

  const [unshareAccountsMutation] =
    advisoryApiV2.useUnshareAdvisoryUsersAccountsMutation();

  const handleUnshareAccount = useCallback(
    async (userId: string, sharedAccount: SharedAccount) => {
      const iban = sharedAccount.iban;
      const institutionId = sharedAccount.institutionId;

      if (!iban || !institutionId) {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t('aggregator.unshare-institution-error', { ns: 'aggregator' }),
          content: t('errors.unhandled-error', { ns: 'errors' }),
          buttons: {
            confirm: {
              show: true,
              text: 'OK',
            },
          },
        });
        return;
      }

      const params: UnshareAdvisoryUsersAccountsParams &
        UnshareAdvisoryUsersAccountsRequestBody & {
          user_id: number;
          institution_id: string;
        } = {
        user_id: Number(userId),
        institution_id: institutionId,
        ibans: [iban],
      };

      try {
        await unshareAccountsMutation(params).unwrap();
        enqueueSnackbar(
          t('aggregator.institution-unshared', { ns: 'aggregator' }),
          { ...commonSnackProps },
        );
      } catch {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t('aggregator.unshare-institution-error', { ns: 'aggregator' }),
          content: t('errors.unhandled-error', { ns: 'errors' }),
          buttons: {
            confirm: {
              show: true,
              text: 'OK',
            },
          },
        });
      }
    },
    [unshareAccountsMutation, enqueueSnackbar, t],
  );

  const onUnshareInstitution = useCallback(
    (userId: string, sharedAccount: SharedAccount) => {
      const institutionName =
        sharedAccount.institutionName ?? sharedAccount.name ?? '';

      showConfirm({
        title: t('aggregator.unshare-institution-title', { ns: 'aggregator' }),
        message: t('aggregator.unshare-institution-message', {
          ns: 'aggregator',
          institutionName,
        }),
        confirmText: t('common.confirm', { ns: 'common' }),
        onConfirm: () => handleUnshareAccount(userId, sharedAccount),
      });
    },
    [showConfirm, t, handleUnshareAccount],
  );

  return {
    advisors: advisorsData,
    onUnshareInstitution,
    onRefresh,
    isRefreshing: status === 'fetching',
    isRefreshDisabled: status === 'pending',
    showTable: status !== 'pending' || hasData,
  };
};
